<template>
  <h2 class="font-title font-bold text-h3 text-secondary mb-6">{{ ui.title }}</h2>

  <form @submit.prevent="submitForm">
    <!-- Nombre completo -->
    <div class="mb-4">
      <label class="block text-p3 font-sans font-semibold text-neutral-dark mb-1">{{ ui.full_name_label }}</label>
      <input
        type="text"
        v-model="form.full_name"
        @blur="validateField('full_name')"
        :class="fieldClass('full_name')"
      />
      <span v-if="errors.full_name" class="block mt-1 text-xs text-red-500">{{ errors.full_name }}</span>
    </div>

    <!-- Email -->
    <div class="mb-4">
      <label class="block text-p3 font-sans font-semibold text-neutral-dark mb-1">{{ ui.email_label }}</label>
      <input
        type="email"
        v-model="form.email"
        @blur="validateField('email')"
        :class="fieldClass('email')"
      />
      <span v-if="errors.email" class="block mt-1 text-xs text-red-500">{{ errors.email }}</span>
    </div>

    <!-- Teléfono (plain input — mask en Fase 7) -->
    <div class="mb-4">
      <label class="block text-p3 font-sans font-semibold text-neutral-dark mb-1">{{ ui.phone_label }}</label>
      <input
        type="tel"
        v-model="form.phone"
        @blur="validateField('phone')"
        :class="fieldClass('phone')"
      />
      <span v-if="errors.phone" class="block mt-1 text-xs text-red-500">{{ errors.phone }}</span>
    </div>

    <!-- Interés (condicional) -->
    <div v-if="showInterest" class="mb-4">
      <label class="block text-p3 font-sans font-semibold text-neutral-dark mb-1">{{ ui.interest_label }}</label>
      <select
        v-model="form.interest"
        @blur="validateField('interest')"
        :class="fieldClass('interest')"
      >
        <option value="">{{ ui.select_option }}</option>
        <option v-for="option in interestOptions" :key="option.value" :value="option.value">
          {{ option.label }}
        </option>
      </select>
      <span v-if="errors.interest" class="block mt-1 text-xs text-red-500">{{ errors.interest }}</span>
    </div>

    <!-- Comentarios -->
    <div class="mb-4">
      <label class="block text-p3 font-sans font-semibold text-neutral-dark mb-1">{{ ui.comments_label }}</label>
      <textarea
        v-model="form.comments"
        @blur="validateField('comments')"
        :class="fieldClass('comments')"
        rows="4"
      ></textarea>
      <span v-if="errors.comments" class="block mt-1 text-xs text-red-500">{{ errors.comments }}</span>
    </div>

    <!-- Adjunto (condicional) -->
    <div v-if="showAttachment" class="mb-4">
      <label class="block text-p3 font-sans font-semibold text-neutral-dark mb-1">{{ ui.attachment_label }}</label>
      <input
        type="file"
        ref="fileInputRef"
        @change="handleFileChange"
        accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
        class="w-full border border-neutral-light rounded-lg px-4 py-3 text-p2 font-sans text-neutral-dark focus:outline-none focus:border-primary transition-colors file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-primary file:text-white hover:file:bg-primary-dark"
      />
      <span v-if="errors.attachment" class="block mt-1 text-xs text-red-500">{{ errors.attachment }}</span>
    </div>

    <button
      type="submit"
      :disabled="loading || hasErrors"
      class="mt-4 bg-primary-dark text-white font-title font-bold text-p2 px-8 py-3 rounded-xl hover:bg-primary transition disabled:opacity-50 disabled:cursor-not-allowed"
    >
      {{ loading ? ui.sending : ui.submit }}
    </button>

    <div v-if="success" class="mt-4 p-3 bg-green-100 border border-green-400 text-green-700 rounded-lg">
      {{ successMessage }}
    </div>

    <div v-if="serverError" class="mt-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded-lg">
      {{ serverError }}
    </div>
  </form>
</template>

<script setup>
import { ref, reactive, computed } from 'vue';
import client from '@website/http/client.js';

const props = defineProps({
  showAttachment: { type: Boolean, default: false },
  showInterest: { type: Boolean, default: false },
  interestOptions: { type: Array, default: () => [] },
  locale: { type: String, default: 'es' },
  validationMessages: { type: Object, default: () => ({}) },
});

const ui = computed(() => props.validationMessages.ui ?? {});
const vm = computed(() => props.validationMessages);

const form = reactive({
  full_name: '',
  email: '',
  phone: '',
  interest: '',
  comments: '',
  attachment: null,
});

const errors = reactive({});
const loading = ref(false);
const success = ref(false);
const successMessage = ref('');
const serverError = ref('');
const fileInputRef = ref(null);

const hasErrors = computed(() => Object.keys(errors).length > 0);

const fieldBaseClass =
  'w-full border rounded-lg px-4 py-3 text-p2 font-sans text-neutral-dark focus:outline-none focus:border-primary transition-colors';

function fieldClass(field) {
  return errors[field] ? `${fieldBaseClass} border-red-500` : `${fieldBaseClass} border-neutral-light`;
}

function validateField(field) {
  const value = form[field];
  delete errors[field];

  switch (field) {
    case 'full_name':
      if (!value.trim()) errors.full_name = vm.value.full_name?.required ?? 'Full name is required';
      else if (value.trim().length < 2) errors.full_name = vm.value.full_name?.min ?? 'Full name must be at least 2 characters';
      break;

    case 'email':
      if (!value.trim()) errors.email = vm.value.email?.required ?? 'Email is required';
      else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) errors.email = vm.value.email?.invalid ?? 'Please enter a valid email address';
      break;

    case 'phone':
      if (!value.trim()) errors.phone = vm.value.phone?.required ?? 'Phone is required';
      else if (!/^[+]?[\d][\d\s\-().]{3,19}$/.test(value.trim())) errors.phone = vm.value.phone?.invalid ?? 'Please enter a valid phone number';
      break;

    case 'interest':
      if (props.showInterest && !value) errors.interest = vm.value.interest?.required ?? 'Interest is required';
      break;

    case 'comments':
      if (!value.trim()) errors.comments = vm.value.comments?.required ?? 'Comments are required';
      else if (value.trim().length < 10) errors.comments = vm.value.comments?.min ?? 'Comments must be at least 10 characters';
      break;
  }
}

function handleFileChange(event) {
  const file = event.target.files[0];
  if (!file) return;

  delete errors.attachment;

  if (file.size > 5 * 1024 * 1024) {
    errors.attachment = vm.value.attachment?.max_size ?? 'File size must not exceed 5MB';
    event.target.value = '';
    return;
  }

  const allowedTypes = [
    'application/pdf',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'image/jpeg',
    'image/jpg',
    'image/png',
  ];

  if (!allowedTypes.includes(file.type)) {
    errors.attachment = vm.value.attachment?.invalid_type ?? 'Only PDF, DOC, DOCX, JPG, JPEG, and PNG files are allowed';
    event.target.value = '';
    return;
  }

  form.attachment = file;
}

async function submitForm() {
  ['full_name', 'email', 'phone', 'comments'].forEach(validateField);
  if (props.showInterest) validateField('interest');

  if (hasErrors.value) return;

  loading.value = true;
  success.value = false;
  serverError.value = '';

  try {
    const formData = new FormData();

    Object.entries(form).forEach(([key, val]) => {
      if (val !== null && val !== '') formData.append(key, val);
    });
    formData.append('locale', props.locale);

    const { data } = await client.post('/api/contact', formData);

    if (data.success) {
      success.value = true;
      successMessage.value = data.message ?? vm.value.success ?? 'Your message was sent successfully!';

      Object.assign(form, {
        full_name: '',
        email: '',
        phone: '',
        interest: '',
        comments: '',
        attachment: null,
      });

      if (fileInputRef.value) fileInputRef.value.value = '';
    } else {
      serverError.value = data.message ?? vm.value.error ?? 'An error occurred. Please try again.';
    }
  } catch (err) {
    serverError.value = err.response?.data?.message ?? vm.value.error ?? 'An error occurred. Please try again.';
  } finally {
    loading.value = false;
  }
}
</script>
